import {
  Button,
  Card,
  CardHeader,
  CheckboxField,
  FormField,
  MultiSelectTags,
  MultiValueInput,
  TextInput,
} from "@constructorfleet/ui/design-system";
import type { BhgPhraseTrigger } from "@constructorfleet/bhg-product-core";
import { useConfirmAction } from "./confirm-action";

export function PhraseTriggerEditor({
  phraseRules,
  setPhraseRules,
  openIndex,
  setOpenIndex,
}: {
  phraseRules: BhgPhraseTrigger[];
  setPhraseRules: React.Dispatch<React.SetStateAction<BhgPhraseTrigger[]>>;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
}) {
  const { requestConfirm, confirmModal } = useConfirmAction();

  return (
    <div className="ds-stack">
      {confirmModal}
      {phraseRules.map((rule, index) => {
        const isOpen = openIndex === index;

        return (
          <Card key={`phrase-rule-${index}`}>
            <CardHeader
              collapsible
              open={isOpen}
              onToggle={() => setOpenIndex(isOpen ? null : index)}
              title={rule.id || `Trigger ${index + 1}`}
              meta={
                <>
                  {rule.phrasePatterns.filter(Boolean).length} pattern
                  {rule.phrasePatterns.filter(Boolean).length !== 1 ? "s" : ""}
                  {rule.oneshot ? " · one-shot" : ""}
                  {rule.hangup ? " · hangup" : ""}
                </>
              }
              action={
                isOpen ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestConfirm({
                        title: "Delete phrase trigger",
                        message: `Delete "${rule.id || `Trigger ${index + 1}`}"? This cannot be undone until you save.`,
                        confirmLabel: "Delete",
                        tone: "danger",
                        onConfirm: () => {
                          setPhraseRules((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index)
                          );
                          setOpenIndex(null);
                        },
                      });
                    }}
                  >
                    Delete
                  </Button>
                ) : undefined
              }
            />
            {isOpen ? (
              <div className="card-body ds-stack">
                <div className="settings-compact-grid">
                  <FormField label="Trigger ID" htmlFor={`phrase-rule-id-${index}`}>
                    <TextInput
                      id={`phrase-rule-id-${index}`}
                      value={rule.id}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, id: nextValue } : item
                          )
                        );
                      }}
                      required
                    />
                  </FormField>
                  <FormField label="Pause seconds" htmlFor={`phrase-rule-pause-${index}`}>
                    <TextInput
                      id={`phrase-rule-pause-${index}`}
                      type="number"
                      min={0}
                      value={rule.pauseSeconds == null ? "" : String(rule.pauseSeconds)}
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        const val = raw.trim().length === 0 ? null : Math.max(0, Number(raw || 0));
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, pauseSeconds: val } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Record seconds" htmlFor={`phrase-rule-record-${index}`}>
                    <TextInput
                      id={`phrase-rule-record-${index}`}
                      type="number"
                      min={0}
                      value={rule.recordSeconds == null ? "" : String(rule.recordSeconds)}
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        const val = raw.trim().length === 0 ? null : Math.max(0, Number(raw || 0));
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, recordSeconds: val } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Send digits" htmlFor={`phrase-rule-send-digits-${index}`}>
                    <TextInput
                      id={`phrase-rule-send-digits-${index}`}
                      value={rule.sendDigits ?? ""}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value || null;
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, sendDigits: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField
                    label="Send digits when invalid"
                    htmlFor={`phrase-rule-send-digits-invalid-${index}`}
                  >
                    <TextInput
                      id={`phrase-rule-send-digits-invalid-${index}`}
                      value={rule.sendDigitsWhenInvalid ?? ""}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value || null;
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, sendDigitsWhenInvalid: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Priority" htmlFor={`phrase-rule-priority-${index}`}>
                    <TextInput
                      id={`phrase-rule-priority-${index}`}
                      type="number"
                      value={String(rule.priority)}
                      onChange={(event) => {
                        const nextValue = Number(event.currentTarget.value || 0);
                        setPhraseRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, priority: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                </div>

                <div style={{ display: "flex", gap: "var(--cf-space-md)" }}>
                  <CheckboxField
                    id={`phrase-rule-oneshot-${index}`}
                    label="One-shot"
                    checked={rule.oneshot}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.checked;
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, oneshot: nextValue } : item
                        )
                      );
                    }}
                  />
                  <CheckboxField
                    id={`phrase-rule-hangup-${index}`}
                    label="Hang up after trigger"
                    checked={rule.hangup}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.checked;
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, hangup: nextValue } : item
                        )
                      );
                    }}
                  />
                </div>

                <FormField label="Phrase patterns" htmlFor={`phrase-rule-patterns-${index}`}>
                  <MultiValueInput
                    id={`phrase-rule-patterns-${index}`}
                    values={rule.phrasePatterns}
                    onChange={(nextValues) =>
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, phrasePatterns: nextValues } : item
                        )
                      )
                    }
                    addButtonLabel="Add phrase pattern"
                    itemAriaLabel={(pi) =>
                      `Pattern ${pi + 1} for ${rule.id || `trigger ${index + 1}`}`
                    }
                    minItems={1}
                  />
                </FormField>

                <FormField label="Verification phrases" htmlFor={`phrase-rule-verify-${index}`}>
                  <MultiValueInput
                    id={`phrase-rule-verify-${index}`}
                    values={rule.verificationPhrases}
                    onChange={(nextValues) =>
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, verificationPhrases: nextValues } : item
                        )
                      )
                    }
                    addButtonLabel="Add verification phrase"
                    itemAriaLabel={(pi) =>
                      `Verification phrase ${pi + 1} for ${rule.id || `trigger ${index + 1}`}`
                    }
                  />
                </FormField>

                <FormField
                  label="Required IDs"
                  htmlFor={`phrase-rule-required-${index}`}
                  helpText="Prior trigger IDs that must already match."
                >
                  <MultiSelectTags
                    id={`phrase-rule-required-${index}`}
                    values={rule.requiredIds}
                    options={phraseRules
                      .map((item) => item.id.trim())
                      .filter((itemId) => itemId.length > 0 && itemId !== rule.id.trim())}
                    onChange={(nextValues) =>
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, requiredIds: nextValues } : item
                        )
                      )
                    }
                    emptyLabel="No required IDs selected."
                  />
                </FormField>

                <FormField
                  label="Reset IDs on invalid"
                  htmlFor={`phrase-rule-reset-${index}`}
                  helpText="Trigger IDs to clear when validation fails."
                >
                  <MultiSelectTags
                    id={`phrase-rule-reset-${index}`}
                    values={rule.resetIdsOnInvalid}
                    options={phraseRules
                      .map((item) => item.id.trim())
                      .filter((itemId) => itemId.length > 0 && itemId !== rule.id.trim())}
                    onChange={(nextValues) =>
                      setPhraseRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, resetIdsOnInvalid: nextValues } : item
                        )
                      )
                    }
                    emptyLabel="No reset IDs selected."
                  />
                </FormField>
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
